import { createRouter } from "@/composables/router"

// routes is a map of Routes the client needs to reference for generating urls for
// page links as well as API calls.
//
// Replacement parameters should be wrapped in braces ex: /users/{id} and only contain letters [a-zA-Z]
// parameter names like {id}, {parentId}, {slug}, {parentSlug} should be used as a general convention to
// indicate the parameter type - number or string.
//
// Always use the {id} and {slug} params as the primary identifier of the primary resource of the route.
// ex: "/products/{parentId}/product-tiers/{id}" => a product tier is the primary resource in this route.
const routes = {
  AccountSetup: "/account-setup",
  AdvisorProfiles: "/advisor-profiles",
  AnnualAdvUpdate: "/annual-adv-update",
  BankAccount: "/bank-accounts/{id}",
  Benchmarking: "/benchmarking",
  Benchmarkings: "/benchmarkings",
  Benefits: "/benefits",
  Community: "/community",
  CouponPendingCoupon: "/coupons/{parentId}/pending-coupons/{id}",
  Coupon_Apply: "/coupons/{id}/apply",
  Coupon_Valid: "/coupons/{slug}/valid",
  Dashboard: "/dashboard",
  EventsCalendar: "/events-calendar",
  ExitInterviews: "/exit-interviews",
  ExitInterview: "/exit-interviews/{id}",
  FindAdvisor: "/find-an-advisor/{slug}",
  FindAdvisors: "/find-an-advisor",
  FindAdvisor_Contact: "/find-an-advisor/{slug}/contact",
  Firm_Convert: "/firms/{id}/convert",
  Firms: "/firms",
  Firm_BankAccounts: "/firms/{parentId}/bank-accounts",
  Group: "/community/groups/{id}",
  Group_GroupMeetings: "/community/groups/{id}/group-meetings",
  Group_MeetingAttendances: "/community/groups/{id}/meeting-attendances",
  Group_MeetingFeedback: "/community/groups/{id}/meeting-feedback",
  Group_Kickoff: "/community/groups/{id}/kickoff",
  Groups: "/community/groups",
  GroupMeeting: "/group-meetings/{id}",
  GroupParticipants: "/group-participants",
  Logoff: "/logoff",
  MastermindOptIn: "/mastermind-opt-ins/{id}",
  MastermindOptIns: "/mastermind-opt-ins",
  MastermindOptInWindow: "/mastermind-opt-in-windows/{id}",
  MastermindOptInWindows: "/mastermind-opt-in-windows",
  MembershipKitRequest: "/membership-kit-requests/{id}",
  MembershipKitRequest_Complete: "/membership-kit-requests/{id}/complete",
  MembershipKitRequest_Reject: "/membership-kit-requests/{id}/reject",
  MembershipKitRequests: "/membership-kit-requests",
  MembershipPrepay_Purchase: "/membership-prepays/{id}/purchase",
  NewApp: "/new-apps/{id}",
  NewApps: "/new-apps",
  NewApp_ConvertToken: "/new-apps/{id}/convert-token",
  NewApplication: "/new-application",
  NewApp_AssociateFirmURL: "/new-apps/{id}/associate-firm",
  Notification: "/notifications/{id}",
  Notifications: "/notifications",
  Offboarding: "/offboarding",
  OneTimePurchase: "/one-time-purchases/{id}",
  OneTimePurchases: "/one-time-purchases",
  PendingCoupons: "pending-coupons",
  PortalEvents: "/portal-events",
  Product: "/products/{id}",
  ProductRequests: "/product-requests",
  ProductRequest: "/product-requests/{id}",
  ProductRequest_Complete: "/product-requests/{id}/complete",
  ProductRequest_Reject: "/product-requests/{id}/reject",
  ProductRequest_Signature: "/product-requests/{id}/signature",
  ProductTier: "/product-tiers/{id}",
  ProductTier_UpdatePrice: "/product-tiers/{id}/update-price",
  Profiles: "/community/profiles", // NOTE: routes to member + staff facing feature
  Purchase: "/purchases/{id}",
  Purchases: "/purchases",
  RawDataUpload: "/raw-data-uploads",
  Root: "/",
  Settings: "/settings",
  SSOSaml2_Academy: "/sso/saml2/academy",
  StaffPermissions: "/staff-permissions",
  StaffPermission: "/staff-permissions/{id}",
  Signature_Complete: "/signatures/{id}/complete",
  SignatureDownload: "/signatures/{id}/download",
  Signatures: "/signatures",
  SubscriptionItem: "/subscription-items/{id}",
  SubscriptionItems: "/subscription-items",
  Support: "/support",
  TodoTemplates: "/todo-templates",
  TodoTemplate: "/todo-templates/{id}",
  Users: "/users",
  UserMessages: "/users/{id}/messages",
  Todos: "/todos",
  Todo: "/todos/{id}",

  // staff subroutes
  Staff: "/staff",
  StaffBenchmarkings: "/staff/benchmarkings",
  StaffBenchmarkings_Download: "/staff/benchmarkings/download",
  StaffCouponPendingCoupon: "/coupons/{parentId}/pending-coupons/{id}",
  StaffExitInterviews: "/staff/exit-interviews",
  StaffExitInterview: "/staff/exit-interviews/{id}",
  StaffFirms: "/staff/firms",
  StaffFirmsROIs: "/staff/firms-rois",
  StaffFirmsROIs_Download: "/staff/firms-rois/download",
  StaffGroup_Conference: "/staff/community/groups/{slug}/conference",
  StaffGroup: "/staff/community/groups/{slug}",
  StaffGroup_GroupMeetings: "/staff/community/groups/{slug}/group-meetings",
  StaffGroups: "/staff/community/groups",
  StaffGroupMeeting: "/staff/group-meetings/{id}",
  StaffGroupParticipant_Leave: "/staff/group-participants/{id}/leave",
  StaffHistForumActivity: "/staff/hist-forum-activity",
  StaffMastermindOptInWindows: "/staff/mastermind-opt-in-windows",
  StaffMembershipKitRequest: "/staff/membership-kit-requests/{id}",
  StaffMembershipKitRequests: "/staff/membership-kit-requests",
  StaffNewApps: "/staff/new-apps",
  StaffNewApp: "/staff/new-apps/{id}",
  StaffNotifications: "/staff/notifications",
  StaffNotification: "/staff/notifications/{id}",
  StaffOneTimePurchases: "/staff/one-time-purchases",
  StaffPendingCoupons: "/staff/pending-coupons",
  StaffPurchases: "/staff/purchases",
  StaffProduct: "/staff/products/{id}",
  StaffProducts: "/staff/products",
  StaffProductRequest: "/staff/product-requests/{id}",
  StaffProductRequests: "/staff/product-requests",
  StaffProductTier: "/staff/products/{parentId}/product-tiers/{id}",
  StaffSettings: "/staff/settings",
  StaffSubscriptionItems: "/staff/subscription-items",
  StaffTodoTemplates: "/staff/todo-templates",
  StaffTodoTemplate: "/staff/todo-templates/{id}",
  StaffTodos: "/staff/todos",
  StaffTodos_Download: "/staff/todos/download",
  StaffTodo: "/staff/todos/{id}",
} as const

export const router = createRouter(routes)
